import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { playNotificationSound, playToastSound } from '../../utils/sounds';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axiosConfig';
import './Dashboard.css';
import PanelPrincipalCliente from '../../components/PanelPrincipalCliente/PanelPrincipalCliente';
import PanelPrincipalAdministrador from '../../components/PanelPrincipalAdministrador/PanelPrincipalAdministrador';
import Calendario from '../../components/Calendario/Calendario';
import Configuracion from '../../components/Configuracion/Configuracion';
import ConfiguracionCliente from '../../components/ConfiguracionCliente/ConfiguracionCliente';
import Entidades from '../../components/Entidades/Entidades';
import CarpetaPersonal from '../../components/CarpetaPersonal/CarpetaPersonal';
import OneDriveAdmin from '../../components/OneDriveAdmin/OneDriveAdmin';
import TramitesCliente from '../../components/TramitesCliente/TramitesCliente';
import AdminTramites from '../../components/AdminTramites/AdminTramites';
import BandejaEntrada from '../../components/BandejaEntrada/BandejaEntrada';
import VistaCarpetaGenerica from '../../components/VistaCarpetaGenerica/VistaCarpetaGenerica';
import MuroAdmin from '../../components/MuroAdmin/MuroAdmin';
import WelcomeNewUser from '../../components/WelcomeNewUser/WelcomeNewUser';
import logo from '../../assets/logo.jpeg';
import { FOLDER_ROUTES, FOLDER_KEYS } from '../../config/folderRoutes';

const CARPETA_ITEM = (tab, titulo) => ({ tab, titulo, items: null });
const CARPETA_GRUPO = (titulo, items) => ({ tab: null, titulo, items });

const SIDEBAR_GRUPO_1 = [
  CARPETA_ITEM('g1-doc-social', 'Documentacion Social'),
  CARPETA_ITEM('g1-doc-socio', 'Documentacion Socio Gerente'),
  CARPETA_ITEM('g1-libro-actas', 'Libro de Actas'),
  CARPETA_ITEM('g1-libro-inventario', 'Libro de Inventario y Balance'),
  CARPETA_ITEM('g1-libro-diario', 'Libro Diario'),
  CARPETA_GRUPO('Impositivo', [
    CARPETA_ITEM('impositivo-arca', 'ARCA'),
    CARPETA_ITEM('impositivo-dpr', 'DPR'),
    CARPETA_ITEM('impositivo-municipalidad', 'Municipalidad'),
  ]),
  CARPETA_GRUPO('Recursos Humanos', [
    CARPETA_ITEM('rh-empadronamiento', 'Empadronamiento'),
    CARPETA_ITEM('rh-legajos', 'Legajos'),
    CARPETA_ITEM('rh-recibos', 'Recibos'),
    CARPETA_ITEM('rh-libro', 'Libro de Sueldos'),
    CARPETA_ITEM('rh-sindicatos', 'Sindicatos'),
    CARPETA_ITEM('rh-art', 'Documentacion ART'),
  ]),
  CARPETA_ITEM('g1-doc-financiera', 'Doc. Financiera - Bancos - Billeteras'),
  CARPETA_ITEM('g1-informes', 'Informes'),
  CARPETA_ITEM('g1-proveedor', 'Proveedor del Estado'),
];

const SIDEBAR_GRUPO_2 = [
  CARPETA_ITEM('g2-doc-personal', 'Documentacion Personal'),
  CARPETA_GRUPO('Impositivo', [
    CARPETA_ITEM('impositivo-arca', 'ARCA'),
    CARPETA_ITEM('impositivo-dpr', 'DPR'),
    CARPETA_ITEM('impositivo-municipalidad', 'Municipalidad'),
  ]),
  CARPETA_GRUPO('Recursos Humanos', [
    CARPETA_ITEM('rh-legajos', 'Legajos'),
    CARPETA_ITEM('rh-recibos', 'Recibos'),
    CARPETA_ITEM('rh-libro', 'Libro de Sueldos'),
    CARPETA_ITEM('rh-sindicatos', 'Sindicatos'),
    CARPETA_ITEM('rh-art', 'Documentacion ART'),
  ]),
  CARPETA_ITEM('g2-doc-financiera', 'Doc. Financiera - Bancos - Billeteras'),
  CARPETA_ITEM('g2-informes', 'Informes'),
  CARPETA_ITEM('g2-proveedor', 'Proveedor del Estado'),
];

export const isAdminRole = (roleStr) => {
  if (!roleStr) return false;
  const role = roleStr.toLowerCase().trim();
  return role === 'admin' || role === 'administrador';
};

export const isClientRole = (roleStr) => {
  if (!roleStr) return false;
  return !isAdminRole(roleStr);
};

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('panel');
  const [user, setUser] = useState(null);
  const [rol, setRol] = useState('');
  const [impersonatingClient, setImpersonatingClient] = useState(null);
  const [expandedProcId, setExpandedProcId] = useState(null);
  
  const [entidadesUsuario, setEntidadesUsuario] = useState([]);
  const [entidadActiva, setEntidadActiva] = useState(null);

  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const userDropdownRef = useRef(null);
  const [toasts, setToasts] = useState([]);

  const addToast = (message, type = 'success') => {
    setToasts(prev => {
      if (prev.some(t => t.message === message && t.type === type)) return prev;
      const id = Date.now() + Math.random();
      playToastSound();
      setTimeout(() => removeToast(id), 3500);
      return [...prev, { id, message, type }];
    });
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  const activeUser = impersonatingClient || user;
  const activeRol = impersonatingClient ? impersonatingClient.rol : rol;
  const sinEntidades = !impersonatingClient && isClientRole(activeRol) && entidadesUsuario.length === 0;

  const [grupoOpen, setGrupoOpen] = useState({});

  const [notificacionesOpen, setNotificacionesOpen] = useState(false);
  const notificacionesRef = useRef(null);
  const prevNotiCountRef = useRef(0);
  
  const [notificaciones, setNotificaciones] = useState([]);
  const [targetEntidad, setTargetEntidad] = useState(null);
  const [targetFolderId, setTargetFolderId] = useState(null);
  const [targetFolderPath, setTargetFolderPath] = useState([]);
  const [carpetasConNovedades, setCarpetasConNovedades] = useState([]);
  const [tramitesNavId, setTramitesNavId] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const activeCuit = entidadActiva?.cuit || activeUser?.cuit;

  useEffect(() => {
    setSidebarOpen(false);
  }, [activeTab]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (sidebarOpen && isMobile) {
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overscrollBehavior = 'contain';
      window._sidebarScrollY = scrollY;
    } else {
      const scrollY = window._sidebarScrollY || 0;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overscrollBehavior = '';
      window.scrollTo(0, scrollY);
      window._sidebarScrollY = undefined;
    }
    return () => {
      const scrollY = window._sidebarScrollY || 0;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overscrollBehavior = '';
      window.scrollTo(0, scrollY);
      window._sidebarScrollY = undefined;
    };
  }, [sidebarOpen, isMobile]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificacionesRef.current && !notificacionesRef.current.contains(event.target)) {
        setNotificacionesOpen(false);
      }
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setUserDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchNotificaciones = useCallback(async () => {
    try {
      const res = await api.get('/obtenerNotificaciones');
      const data = res.data?.data || res.data || [];
      setNotificaciones(Array.isArray(data) ? data : []);
    } catch (err) {
      setNotificaciones([]);
    }
  }, []);

  const fetchCarpetasConNovedades = useCallback(async () => {
    try {
      const res = await api.post('/obtenerCarpetasConNovedades');
      if (res.data?.carpetas) setCarpetasConNovedades(res.data.carpetas);
    } catch (err) {
      // silent
    }
  }, []);

  useEffect(() => {
    fetchNotificaciones();
    fetchCarpetasConNovedades();
    const interval = setInterval(() => {
      fetchNotificaciones();
      fetchCarpetasConNovedades();
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchNotificaciones, fetchCarpetasConNovedades]);

  useEffect(() => {
    if (prevNotiCountRef.current > 0 && notificaciones.length > prevNotiCountRef.current) {
      playNotificationSound();
    }
    prevNotiCountRef.current = notificaciones.length;
  }, [notificaciones.length]);

  const eliminarNotificacion = async (id) => {
    try {
      await api.delete('/eliminarNotificacion', { data: { id } });
      fetchNotificaciones();
    } catch (err) {
      console.error('Error al eliminar notificacion:', err);
    }
  };

  const parseFecha = (fecha, hora) => {
    if (!fecha) return null;
    const dateStr = fecha.includes('T') ? fecha.split('T')[0] : fecha;
    const timeStr = (hora || '00:00').split(':').slice(0, 2).join(':');
    return new Date(`${dateStr}T${timeStr}`);
  };

  const formatRelTime = (fecha, hora) => {
    const date = parseFecha(fecha, hora);
    if (!date || isNaN(date.getTime())) return fecha || '';
    const now = new Date();
    const diffMs = now - date;
    const diffMin = Math.floor(diffMs / 60000);
    const diffHrs = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMin < 1) return 'Ahora';
    if (diffMin < 60) return `Hace ${diffMin} min`;
    if (diffHrs < 24) return `Hace ${diffHrs} hs`;
    if (diffDays === 1) return 'Ayer';
    if (diffDays < 7) return `Hace ${diffDays} dias`;
    return date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatFechaAbs = (fecha, hora) => {
    const date = parseFecha(fecha, hora);
    if (!date || isNaN(date.getTime())) return `${fecha || ''} ${hora || ''}`;
    return date.toLocaleDateString('es-AR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const totalNotis = notificaciones.length;

  const getNotiInfo = (noti) => {
    const tabFromDesc = noti.descripcion?.match(/TAB:([^|]+)/);
    if (tabFromDesc) {
      return { icon: 'bi-folder2', color: '#d97706', bg: '#fffbeb', tab: tabFromDesc[1] };
    }
    // SIN TAB en descripcion
    const t = (noti.titulo || '').toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    if (t.includes('tramite')) {
      return { icon: 'bi-clipboard-check', color: '#2a5a8a', bg: '#ebf4ff', tab: isAdminRole(activeRol) ? 'admin-tramites' : 'tramites' };
    }
    if (t.includes('documento') || t.includes('carpeta')) {
      if (isAdminRole(activeRol)) return { icon: 'bi-folder2', color: '#d97706', bg: '#fffbeb', tab: 'onedrive-global' };
      const match = noti.titulo.match(/en\s+(.+)/);
      const folder = match ? match[1].toLowerCase().trim() : '';
      const map = {
        arca: 'impositivo-arca', dpr: 'impositivo-dpr', municipalidad: 'impositivo-municipalidad',
        legajos: 'rh-legajos', 'recibos de sueldos': 'rh-recibos', 'libro de sueldos': 'rh-libro',
        sindicatos: 'rh-sindicatos', 'documentacion art': 'rh-art',
        empadronamiento: 'rh-empadronamiento'
      };
      const key = Object.keys(map).find(k => folder.includes(k));
      const tab = key ? map[key] : null;
      return { icon: 'bi-folder2', color: '#d97706', bg: '#fffbeb', tab };
    }
    if (t.includes('archivo')) {
      return { icon: 'bi-upload', color: '#0891b2', bg: '#ecfeff', tab: 'bandeja' };
    }
    if (t.includes('factura') || t.includes('pago') || t.includes('honorario')) return { icon: 'bi-cash-coin', color: '#1b7a4a', bg: '#f0fff4', tab: null };
    if (t.includes('recordatorio')) return { icon: 'bi-calendar-check', color: '#8b5cf6', bg: '#f5f3ff', tab: 'calendario' };
    return { icon: 'bi-megaphone', color: '#7c3aed', bg: '#f5f3ff', tab: null };
  };

  const navigate = useNavigate();

  const handleLogout = useCallback(() => {
    localStorage.removeItem('token');
    navigate('/login');
  }, [navigate]);

  const fetchUser = useCallback(async (signal) => {
    try {
      const options = signal ? { signal } : undefined;
      const res = await api.get('/obtenerUsuario', options);
      const resData = res.data;
      const entidadesIds = resData.entidades || [];
      console.log('Entidades asociadas al usuario (IDs):', entidadesIds);

      const entidadesDetalles = await Promise.all(
        entidadesIds.map(async (id) => {
          try {
            const entRes = await api.get('/obtenerEntidadConId', { 
              ...options, 
              params: { entidadId: id },
              data: { entidadId: id }
            });
            return { id, ...((entRes.data.data && entRes.data.data[0]) || entRes.data.data || entRes.data) };
          } catch (e) {
            console.error('Error fetching entidad:', id, e);
            return null;
          }
        })
      );

      const entidadesValidas = entidadesDetalles.filter(e => e !== null);
      setEntidadesUsuario(entidadesValidas);

      const payload = (resData && resData.user) || (resData && resData.data && resData.data.user) || (resData && resData.data) || resData;
      console.log('Datos del usuario obtenidos:', payload);
      setUser(payload);
      setRol(payload.rol || '');

      if (!entidadActiva && entidadesValidas.length > 0) {
        setEntidadActiva(entidadesValidas[0]);
      }
    } catch (err) {
      if (err && (err.name === 'CanceledError' || err.name === 'AbortError')) {
        console.log('Peticion cancelada (limpieza normal de React)');
      } else {
        console.error(err);
        handleLogout();
      }
    }
  }, [handleLogout, entidadActiva]);

  useEffect(() => {
    const controller = new AbortController();
    fetchUser(controller.signal);
    return () => controller.abort();
  }, [fetchUser]);

  const handleCambiarEntidad = (entidad) => {
    setEntidadActiva(entidad);
    setUserDropdownOpen(false);
    setGrupoOpen({});
    
    setActiveTab('panel');
    
    addToast(`Ahora estas operando como: ${entidad.razonSocial || entidad.nombre}`);
  };

  const renderContent = () => {
    switch(activeTab) {
      case 'panel':
        if (isAdminRole(activeRol)) {
          return (
            <PanelPrincipalAdministrador user={activeUser} entidad={entidadActiva} onNavigate={(entidadId) => { setTargetEntidad(entidadId); setTramitesNavId(prev => prev + 1); setActiveTab('admin-tramites'); }} />
          );
        }
        if (sinEntidades) {
          return <WelcomeNewUser />;
        }
        return (
          <PanelPrincipalCliente user={activeUser} entidad={entidadActiva} onNavigate={(procId) => { setExpandedProcId(procId || null); setActiveTab('tramites'); }} />
        );
       case 'muro':
         return isAdminRole(activeRol) ? <MuroAdmin addToast={addToast} /> : null;
       case 'entidades':
        return <Entidades onImpersonate={isAdminRole(activeRol) ? (entidad) => {
          setImpersonatingClient(entidad);
          setEntidadActiva(entidad);
          setActiveTab('panel');
        } : undefined} isAdmin={isAdminRole(activeRol)} entidadCuit={isAdminRole(activeRol) ? undefined : activeUser?.cuit} addToast={addToast} />;
      case 'carpeta-personal':
        return (
          <CarpetaPersonal 
            crearNotificacion={setNotificaciones} 
            notificaciones={notificaciones} 
            carpetaId={activeUser?.carpeta_id} 
            nombreCliente={activeUser?.nombre || activeUser?.razonSocial}
            entidadCuit={activeCuit}
            entidadId={entidadActiva?.id}
            isAdmin={isAdminRole(activeRol)}
            addToast={addToast}
            tabKey={activeTab}
            targetFolderId={targetFolderId}
            targetFolderPath={targetFolderPath}
            carpetasConNovedades={carpetasConNovedades}
          />
        );
      case 'bandeja':
          return <BandejaEntrada entidadId={entidadActiva?.id} targetEntidadId={targetEntidad} isAdmin={isAdminRole(activeRol)} addToast={addToast} />;
      case 'tramites':
        return <TramitesCliente entidadId={entidadActiva?.id} cuit={activeCuit} addToast={addToast} initialExpandId={expandedProcId} onExpanded={() => setExpandedProcId(null)} />;
      case 'admin-tramites':
        return isAdminRole(activeRol) ? <AdminTramites key={'admin-tramites-nav-' + tramitesNavId} addToast={addToast} targetEntidad={targetEntidad} onNavigate={() => setTargetEntidad(null)} /> : null;
      case 'calendario':
        return <Calendario entidadId={impersonatingClient ? entidadActiva?.id : undefined} />;
      case 'onedrive-global':
        return isAdminRole(activeRol) ? (
          <OneDriveAdmin 
            crearNotificacion={setNotificaciones} 
            notificaciones={notificaciones} 
            addToast={addToast}
            entidadCuit={activeCuit}
            entidadId={entidadActiva?.id}
            tabKey={activeTab}
          />
        ) : null;
      case 'configuracion':
        return isAdminRole(activeRol) ? (
          <Configuracion user={activeUser} fetchUser={fetchUser} handleLogout={handleLogout} addToast={addToast} />
        ) : (
          <ConfiguracionCliente user={activeUser} fetchUser={fetchUser} handleLogout={handleLogout} addToast={addToast} />
        );
      
      default:
        if (FOLDER_KEYS.includes(activeTab)) {
          const folder = FOLDER_ROUTES[activeTab];
          return (
            <VistaCarpetaGenerica
              key={activeTab}
              titulo={folder.titulo}
              crearNotificacion={setNotificaciones}
              notificaciones={notificaciones}
              carpetaId={entidadActiva?.carpeta_id || activeUser?.carpeta_id}
              nombreCliente={entidadActiva?.razonSocial || activeUser?.nombre || activeUser?.razonSocial}
              isAdmin={isAdminRole(activeRol)}
              puedeSubir={isAdminRole(activeRol)}
              startPath={folder.startPath}
              entidadCuit={entidadActiva?.cuit || activeUser?.cuit}
              entidadId={entidadActiva?.id}
              addToast={addToast}
              tabKey={activeTab}
              targetFolderId={targetFolderId}
              targetFolderPath={targetFolderPath}
              carpetasConNovedades={carpetasConNovedades}
            />
          );
        }
        return isAdminRole(activeRol) ? <PanelPrincipalAdministrador user={activeUser} onNavigate={(entidadId) => { setTargetEntidad(entidadId); setTramitesNavId(prev => prev + 1); setActiveTab('admin-tramites'); }} /> : <PanelPrincipalCliente user={activeUser} entidad={entidadActiva} />;
    }
  };

  const clientSidebarItems = useMemo(() => {
    if (!isClientRole(activeRol) || !entidadActiva?.grupo) return null;
    const items = entidadActiva.grupo === 1 ? SIDEBAR_GRUPO_1 : SIDEBAR_GRUPO_2;
    return items;
  }, [entidadActiva?.grupo, activeRol]);

  if (!user) return <div className="loading-container">Cargando datos del sistema...</div>;

  return (
    <div className={`dashboard-layout ${impersonatingClient ? 'impersonation-mode' : ''} ${sidebarOpen ? 'sidebar-open' : ''}`}>
      <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)}></div>
      <aside className="dashboard-sidebar">
        <div className="sidebar-header">
            <img src={logo} alt="Estudio Metzger" className="logo-image" onClick={() => setActiveTab('panel')} />
        </div>
        <nav className="sidebar-nav">
          <div className={`nav-item ${activeTab === 'panel' ? 'active' : ''}`} onClick={() => setActiveTab('panel')}>
            <i className="bi bi-graph-up nav-icon"></i> Inicio
          </div>

          {isAdminRole(activeRol) && (
            <>
              <div className={`nav-item ${activeTab === 'muro' ? 'active' : ''}`} onClick={() => setActiveTab('muro')}>
                <i className="bi bi-megaphone nav-icon"></i> Muro
              </div>
              <div className={`nav-item ${activeTab === 'entidades' ? 'active' : ''}`} onClick={() => setActiveTab('entidades')}>
                <i className="bi bi-building nav-icon"></i> Entidades
              </div>
              <div className={`nav-item ${activeTab === 'onedrive-global' ? 'active' : ''}`} onClick={() => setActiveTab('onedrive-global')}>
                <i className="bi bi-cloud nav-icon"></i> OneDrive
              </div>
              <div className={`nav-item ${activeTab === 'admin-tramites' ? 'active' : ''}`} onClick={() => setActiveTab('admin-tramites')}>
                <i className="bi bi-clipboard-check nav-icon"></i> Trámites
              </div>
              <div className={`nav-item ${activeTab === 'calendario' ? 'active' : ''}`} onClick={() => setActiveTab('calendario')}>
                <i className="bi bi-calendar3 nav-icon"></i> Calendario
              </div>
              <div className={`nav-item ${activeTab === 'configuracion' ? 'active' : ''}`} onClick={() => setActiveTab('configuracion')}>
                <i className="bi bi-gear nav-icon"></i> Configuracion
              </div>
            </>
          )}

          {isClientRole(activeRol) && sinEntidades && (
            <div className={`nav-item ${activeTab === 'configuracion' ? 'active' : ''}`} onClick={() => setActiveTab('configuracion')}>
              <i className="bi bi-gear nav-icon"></i> Configuracion
            </div>
          )}

          {isClientRole(activeRol) && !sinEntidades && clientSidebarItems && (
            <>
              {clientSidebarItems.map((item, idx) => {
                  if (item.items) {
                    const isOpen = grupoOpen[idx] || false;
                    return (
                      <div className="nav-item-dropdown" key={idx}>
                        <div className="nav-item" onClick={() => setGrupoOpen(prev => ({ ...prev, [idx]: !isOpen }))}
                          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <i className={`bi bi-folder nav-icon`}></i> {item.titulo}
                          </div>
                          <i className={`bi ${isOpen ? 'bi-chevron-down' : 'bi-chevron-right'}`} style={{ fontSize: '12px', opacity: 0.6 }}></i>
                        </div>
                        {isOpen && (
                          <div className="dropdown-content" style={{ display: 'flex', flexDirection: 'column' }}>
                            {item.items.map(sub => (
                              <div key={sub.tab} className={`nav-item sub-item ${activeTab === sub.tab ? 'active' : ''}`}
                                onClick={() => setActiveTab(sub.tab)}>
                                {sub.titulo}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  }
                  return (
                    <div key={idx} className={`nav-item ${activeTab === item.tab ? 'active' : ''}`} onClick={() => item.tab && setActiveTab(item.tab)}>
                      <i className="bi bi-folder nav-icon"></i> {item.titulo}
                    </div>
                  );
                })}

              <div className={`nav-item ${activeTab === 'bandeja' ? 'active' : ''}`} onClick={() => setActiveTab('bandeja')}>
                <i className="bi bi-inbox nav-icon"></i> Bandeja
              </div>
              <div className={`nav-item ${activeTab === 'tramites' ? 'active' : ''}`} onClick={() => setActiveTab('tramites')}>
                <i className="bi bi-clipboard-check nav-icon"></i> Trámites
              </div>
              <div className={`nav-item ${activeTab === 'calendario' ? 'active' : ''}`} onClick={() => setActiveTab('calendario')}>
                <i className="bi bi-calendar3 nav-icon"></i> Calendario
              </div>
              
              <div className={`nav-item ${activeTab === 'configuracion' ? 'active' : ''}`} onClick={() => setActiveTab('configuracion')}>
                <i className="bi bi-gear nav-icon"></i> Configuracion
              </div>
            </>
          )}
        </nav>
      </aside>

      <main className="dashboard-main">
        {impersonatingClient && (
          <div className="impersonation-banner">
            <span><i className="bi bi-exclamation-triangle" style={{ marginRight: 8 }}></i> Estas visualizando el sistema como el cliente: <strong>{impersonatingClient.nombre}</strong></span>
            <button onClick={() => {
              setImpersonatingClient(null);
              setActiveTab('entidades');
            }} className="btn-exit-impersonation">Volver a mi sesion</button>
          </div>
        )}

        <header className="dashboard-topbar">
          <button className="sidebar-toggle-btn" onClick={() => { setSidebarOpen(true); setNotificacionesOpen(false); setUserDropdownOpen(false); }} aria-label="Abrir menú">
            <i className="bi bi-list"></i>
          </button>
          <div className="topbar-spacer"></div>
          <div className="topbar-actions">
            <div className="notificaciones-container" ref={notificacionesRef}>
              <div 
                className="notificaciones-trigger"
                onClick={() => { setNotificacionesOpen(!notificacionesOpen); fetchNotificaciones(); if (isMobile) setSidebarOpen(false); }}
              >
                <i className="bi bi-bell" style={{ fontSize: '1.3rem' }}></i>
                {totalNotis > 0 && (
                  <span className="notificaciones-badge">{totalNotis}</span>
                )}
              </div>

              {!isMobile && notificacionesOpen && (
                <div className="notificaciones-dropdown">
                  <div className="notificaciones-header-dropdown">
                    <h4>Notificaciones</h4>
                    {notificaciones.length > 0 && <span className="noti-header-count">{notificaciones.length}</span>}
                  </div>
                  
                  <div className="notificaciones-body">
                    {notificaciones.length === 0 ? (
                      <div className="notificaciones-empty">
                        <i className="bi bi-inbox"></i>
                        <p>No tienes notificaciones</p>
                      </div>
                    ) : (
                      <ul className="notificaciones-list">
                        {notificaciones.map((noti) => {
                          const info = getNotiInfo(noti);
                          return (
                            <li
                              key={noti.id}
                              className={`notificacion-item ${info.tab ? 'clickable' : ''}`}
                                onClick={() => {
                                if (info.tab) {
                                  setActiveTab(info.tab);
                                  setNotificacionesOpen(false);
                                  const entMatch = noti.descripcion?.match(/ENT:(\d+)/);
                                  if (entMatch) {
                                    const entId = Number(entMatch[1]);
                                    if (info.tab === 'bandeja') {
                                      const entidad = entidadesUsuario.find(e => e.id === entId);
                                      if (entidad) setEntidadActiva(entidad);
                                      setTargetEntidad(entId);
                                      setTimeout(() => setTargetEntidad(null), 5000);
                                    } else {
                                      setTargetEntidad(entId);
                                      setTramitesNavId(prev => prev + 1);
                                      const folderMatch = noti.descripcion?.match(/FOLDER:([^|]+)/);
                                      if (folderMatch) {
                                        setTargetFolderId(folderMatch[1]);
                                      }
                                    }
                                  }
                                }
                              }}
                            >
                              <div className="noti-icon-wrap" style={{ backgroundColor: info.bg }}>
                                <i className={`bi ${info.icon}`} style={{ color: info.color }}></i>
                              </div>
                              <div className="noti-content">
                                <div className="noti-title">{noti.titulo}</div>
                                {noti.descripcion && <div className="noti-desc">{noti.descripcion.replace(/^(?:TAB:[^|]*\||FOLDER:[^|]*\||ENT:\d+\||REC:\d+\||RUTA:(?:[^|]+\|)+)+/, '')}</div>}
                                <div className="noti-time-row">
                                  <span className="noti-time-rel">{formatRelTime(noti.fecha, noti.hora)}</span>
                                  <span className="noti-time-abs">{formatFechaAbs(noti.fecha, noti.hora)}</span>
                                </div>
                              </div>
                              <button
                                className="noti-delete"
                                onClick={(e) => { e.stopPropagation(); eliminarNotificacion(noti.id); }}
                                title="Eliminar"
                              >
                                <i className="bi bi-x"></i>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </div>
              )}

              {isMobile && notificacionesOpen && (
                <div className="mobile-modal-overlay" onClick={() => setNotificacionesOpen(false)}>
                  <div className="mobile-modal" onClick={e => e.stopPropagation()}>
                    <div className="mobile-modal-header">
                      <h4><i className="bi bi-bell" style={{ marginRight: 8 }}></i>Notificaciones</h4>
                      <button onClick={() => setNotificacionesOpen(false)}><i className="bi bi-x-lg"></i></button>
                    </div>
                    <div className="mobile-modal-body">
                      {notificaciones.length === 0 ? (
                        <div className="notificaciones-empty">
                          <i className="bi bi-inbox"></i>
                          <p>No tienes notificaciones</p>
                        </div>
                      ) : (
                        <ul className="notificaciones-list">
                          {notificaciones.map((noti) => {
                            const info = getNotiInfo(noti);
                            return (
                              <li
                                key={noti.id}
                                className={`notificacion-item ${info.tab ? 'clickable' : ''}`}
                                  onClick={() => {
                                    if (info.tab) {
                                      setActiveTab(info.tab);
                                      setNotificacionesOpen(false);
                                      const entMatch = noti.descripcion?.match(/ENT:(\d+)/);
                                      if (entMatch) {
                                        const entId = Number(entMatch[1]);
                                        if (info.tab === 'bandeja') {
                                          const entidad = entidadesUsuario.find(e => e.id === entId);
                                          if (entidad) setEntidadActiva(entidad);
                                          setTargetEntidad(entId);
                                          setTimeout(() => setTargetEntidad(null), 5000);
                                        } else {
                                          setTargetEntidad(entId);
                                          setTramitesNavId(prev => prev + 1);
                                          const folderMatch = noti.descripcion?.match(/FOLDER:([^|]+)/);
                                          if (folderMatch) setTargetFolderId(folderMatch[1]);
                                        }
                                      }
                                    }
                                  }}
                              >
                                <div className="noti-icon-wrap" style={{ backgroundColor: info.bg }}>
                                  <i className={`bi ${info.icon}`} style={{ color: info.color }}></i>
                                </div>
                                <div className="noti-content">
                                  <div className="noti-title">{noti.titulo}</div>
{noti.descripcion && <div className="noti-desc">{noti.descripcion.replace(/^(?:TAB:[^|]*\||FOLDER:[^|]*\||ENT:\d+\||REC:\d+\||RUTA:(?:[^|]+\|)+)+/, '')}</div>}
                                  <div className="noti-time-row">
                                    <span className="noti-time-rel">{formatRelTime(noti.fecha, noti.hora)}</span>
                                    <span className="noti-time-abs">{formatFechaAbs(noti.fecha, noti.hora)}</span>
                                  </div>
                                </div>
                                <button
                                  className="noti-delete"
                                  onClick={(e) => { e.stopPropagation(); eliminarNotificacion(noti.id); }}
                                  title="Eliminar"
                                >
                                  <i className="bi bi-x"></i>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="user-profile-dropdown-container" ref={userDropdownRef} style={{ position: 'relative' }}>
              {isAdminRole(activeRol) ? (
                <div className="user-profile-mini" style={{ cursor: 'default' }}>
                  <div className="avatar">
                    {(activeUser.nombre ? activeUser.nombre.charAt(0).toUpperCase() : 'U')}
                  </div>
                  <span>{activeUser.nombre || 'Usuario'}</span>
                </div>
              ) : (<>
              <div className="user-profile-mini" onClick={() => setUserDropdownOpen(!userDropdownOpen)} style={{ cursor: 'pointer' }}>
                <div className="avatar">
                  {entidadActiva 
                    ? (entidadActiva.razonSocial || entidadActiva.nombre || 'E').charAt(0).toUpperCase() 
                    : (activeUser.nombre ? activeUser.nombre.charAt(0).toUpperCase() : 'U')}
                </div>
                <span>{entidadActiva ? (entidadActiva.razonSocial || entidadActiva.nombre) : (activeUser.nombre || 'Usuario')} <i className="bi bi-chevron-down" style={{ fontSize: '12px', marginLeft: 4 }}></i></span>
              </div>
              
              {!isMobile && userDropdownOpen && (
                <div className="user-dropdown-menu">
                  <h5 style={{ margin: '0 0 10px 0', borderBottom: '1px solid var(--border)', paddingBottom: '8px', color: 'var(--text-muted)', fontSize: '14px', fontWeight: 600 }}>Mis Entidades</h5>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, maxHeight: '280px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {entidadesUsuario.map((ent, idx) => (
                      <li 
                        key={idx} 
                        onClick={() => handleCambiarEntidad(ent)}
                        style={{ 
                          padding: '10px 12px', 
                          cursor: 'pointer', 
                          backgroundColor: entidadActiva && entidadActiva.id === ent.id ? '#ebf4ff' : '#fafafa', 
                          border: entidadActiva && entidadActiva.id === ent.id ? '1px solid #bfdbfe' : '1px solid #eee',
                          borderRadius: '6px',
                          display: 'flex',
                          flexDirection: 'column',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => { if (!entidadActiva || entidadActiva.id !== ent.id) e.currentTarget.style.backgroundColor = '#f0f0f0'; }}
                        onMouseLeave={(e) => { if (!entidadActiva || entidadActiva.id !== ent.id) e.currentTarget.style.backgroundColor = '#fafafa'; }}
                      >
                        <span style={{ fontWeight: 600, fontSize: '14px', color: 'var(--text)' }}>
                          {ent.razonSocial || ent.nombre}
                        </span>
                        <span style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
                          CUIT: {ent.cuit}
                        </span>
                      </li>
                    ))}
                  </ul>
                  </div>
                )}

              {isMobile && userDropdownOpen && (
                <div className="mobile-modal-overlay" onClick={() => setUserDropdownOpen(false)}>
                  <div className="mobile-modal" onClick={e => e.stopPropagation()}>
                    <div className="mobile-modal-header">
                      <h4><i className="bi bi-building" style={{ marginRight: 8 }}></i>Mis Entidades</h4>
                      <button onClick={() => setUserDropdownOpen(false)}><i className="bi bi-x-lg"></i></button>
                    </div>
                    <div className="mobile-modal-body">
                      {entidadesUsuario.length === 0 ? (
                        <div className="notificaciones-empty">
                          <i className="bi bi-inbox"></i>
                          <p>No tienes entidades asociadas</p>
                        </div>
                      ) : (
                        <ul className="mobile-entity-list">
                          {entidadesUsuario.map((ent, idx) => (
                            <li
                              key={idx}
                              className={`mobile-entity-item ${entidadActiva && entidadActiva.id === ent.id ? 'active' : ''}`}
                              onClick={() => handleCambiarEntidad(ent)}
                              style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 12 }}
                            >
                              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <span className="mobile-entity-name">{ent.razonSocial || ent.nombre}</span>
                                <span className="mobile-entity-cuit">CUIT: {ent.cuit}</span>
                              </div>
                              {entidadActiva && entidadActiva.id === ent.id && (
                                <i className="bi bi-check-circle-fill mobile-entity-check"></i>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
            </div>
            <button onClick={handleLogout} className="btn btn-outline" style={{ padding: '6px 14px', fontSize: '13px' }}>
              <i className="bi bi-box-arrow-right"></i> Salir
            </button>
          </div>
        </header>

        {renderContent()}
        
        <div style={{
          position: 'fixed',
          bottom: '30px',
          right: '30px',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          gap: '10px'
        }}>
          {toasts.map(toast => {
            const toastColors = {
              success: { bg: '#1b7a4a', icon: 'bi-check-circle-fill' },
              error: { bg: '#c0392b', icon: 'bi-x-circle-fill' },
              warning: { bg: '#d97706', icon: 'bi-exclamation-circle-fill' }
            };
            const colors = toastColors[toast.type] || toastColors.success;
            return (
            <div key={toast.id} style={{
              backgroundColor: colors.bg,
              color: 'white',
              padding: '14px 40px 14px 20px',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              fontSize: '14px',
              fontWeight: 500,
              position: 'relative',
              animation: 'fadeInToast 0.3s ease-in-out',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <i className={`bi ${colors.icon}`}></i>
              {toast.message}
              <button 
                onClick={() => removeToast(toast.id)}
                style={{
                  position: 'absolute', top: '50%', right: '10px', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', color: 'white', cursor: 'pointer',
                  fontSize: '18px', fontWeight: 'bold', padding: 0, lineHeight: 1
                }}
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            );
          })}
          <style>{`
            @keyframes fadeInToast {
              0% { opacity: 0; transform: translateX(50px); }
              100% { opacity: 1; transform: translateX(0); }
            }
          `}</style>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
